import React, { useEffect, useRef, useState } from 'react'
import {
  ChatCard,
  ChatMessage,
  ChatReply,
  PlaceOrderPayload,
  tradingServices,
} from '../../services/trading-service'

const MAX_HISTORY = 20

const usd = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

type Entry = ChatMessage & { cards?: ChatCard[] }

type OrderCardProps = {
  card: Extract<ChatCard, { type: 'order' }>
  onResult: (note: string) => void
  onPlaced: () => void
}

/** The only place an order is sent: the investor has to press Confirm. */
const OrderCard = ({ card, onResult, onPlaced }: OrderCardProps) => {
  const { order } = card
  const [state, setState] = useState<'idle' | 'placing' | 'placed' | 'cancelled'>('idle')
  const [error, setError] = useState('')

  const buying = order.side === 'buy'
  const short =
    buying &&
    card.estimatedTotal !== null &&
    card.buyingPower !== null &&
    card.estimatedTotal > Number(card.buyingPower)
  const label = `${buying ? 'Buy' : 'Sell'} ${order.symbol}`
  const size = order.qty !== undefined ? `${order.qty} shares` : usd(order.notional)

  const confirm = async () => {
    setState('placing')
    setError('')
    const payload: PlaceOrderPayload = order
    const res = await tradingServices.placeOrder(payload)
    if (res?.status === 201) {
      setState('placed')
      onResult(`Order submitted: ${label.toLowerCase()}, ${size} (${res.data.status}).`)
      onPlaced()
    } else {
      setState('idle')
      setError(errorMessage(res, 'Order was not accepted.'))
    }
  }

  const cancel = () => {
    setState('cancelled')
    onResult(`Order cancelled by the investor: ${label.toLowerCase()}, ${size}.`)
  }

  return (
    <div className='trading-chat__card' data-testid='chat-order-card'>
      <div className='trading-chat__card-head'>
        <strong>{buying ? 'Buy order' : 'Sell order'}</strong>
        <span className='trading-chat__pill'>{order.symbol}</span>
      </div>
      <p className='trading-chat__card-title'>{card.name || order.symbol}</p>
      <dl className='trading-chat__facts'>
        <div>
          <dt>Size</dt>
          <dd>{size}</dd>
        </div>
        <div>
          <dt>{order.type === 'limit' ? 'Limit price' : 'Est. price'}</dt>
          <dd>{usd(order.type === 'limit' ? order.limitPrice : card.estimatedPrice)}</dd>
        </div>
        <div>
          <dt>Order total</dt>
          <dd>{usd(card.estimatedTotal)}</dd>
        </div>
        <div>
          <dt>Available cash</dt>
          <dd>{usd(card.buyingPower)}</dd>
        </div>
      </dl>
      {short ? (
        <p className='trading-page__error' role='alert'>
          Not enough buying power for this order.
        </p>
      ) : null}
      {error ? (
        <p className='trading-page__error' role='alert'>
          {error}
        </p>
      ) : null}
      {state === 'placed' ? <p className='trading-page__ok'>Order submitted.</p> : null}
      {state === 'cancelled' ? <p>Cancelled.</p> : null}
      {state === 'idle' || state === 'placing' ? (
        <div className='trading-chat__actions'>
          <button
            type='button'
            className='trading-page__btn'
            onClick={() => void confirm()}
            disabled={state === 'placing' || short}
          >
            {state === 'placing' ? 'Submitting…' : 'Confirm'}
          </button>
          <button
            type='button'
            className='trading-chat__secondary'
            onClick={cancel}
            disabled={state === 'placing'}
          >
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  )
}

const InfoCard = ({ card }: { card: Exclude<ChatCard, { type: 'order' }> }) => {
  switch (card.type) {
    case 'quote':
      return (
        <div className='trading-chat__card'>
          <div className='trading-chat__card-head'>
            <strong>{card.name || card.symbol}</strong>
            <span className='trading-chat__pill'>{card.symbol}</span>
          </div>
          <dl className='trading-chat__facts'>
            <div>
              <dt>Bid</dt>
              <dd>{usd(card.bid)}</dd>
            </div>
            <div>
              <dt>Ask</dt>
              <dd>{usd(card.ask)}</dd>
            </div>
          </dl>
        </div>
      )
    case 'news':
      return (
        <div className='trading-chat__card'>
          <div className='trading-chat__card-head'>
            <strong>Recent news</strong>
            <span className='trading-chat__pill'>{card.symbol}</span>
          </div>
          <ul className='trading-chat__news'>
            {card.items.map((n) => (
              <li key={`${n.date}-${n.headline}`}>
                {/^https?:\/\//.test(n.url ?? '') ? (
                  <a href={n.url as string} target='_blank' rel='noopener noreferrer'>
                    {n.headline}
                  </a>
                ) : (
                  n.headline
                )}
                <small>
                  {n.source}
                  {n.date ? ` · ${new Date(n.date).toLocaleDateString()}` : ''}
                </small>
              </li>
            ))}
          </ul>
        </div>
      )
    case 'positions':
      return (
        <div className='trading-chat__card'>
          <strong>Your positions</strong>
          <table className='trading-page__table'>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Shares</th>
                <th>Value</th>
                <th>P/L</th>
              </tr>
            </thead>
            <tbody>
              {card.positions.map((p) => (
                <tr key={p.symbol}>
                  <td>{p.symbol}</td>
                  <td>{p.qty}</td>
                  <td>{usd(p.marketValue)}</td>
                  <td className={Number(p.unrealizedPl) < 0 ? 'is-loss' : 'is-gain'}>
                    {usd(p.unrealizedPl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'account':
      return (
        <div className='trading-chat__card'>
          <strong>Public wallet</strong>
          <dl className='trading-chat__facts'>
            <div>
              <dt>Cash</dt>
              <dd>{usd(card.cash)}</dd>
            </div>
            <div>
              <dt>Buying power</dt>
              <dd>{usd(card.buyingPower)}</dd>
            </div>
          </dl>
        </div>
      )
    default:
      return null
  }
}

const GREETING: Entry = {
  role: 'assistant',
  content:
    'Hi! Ask me for a price, the latest news, your positions or cash, or tell me what to buy or sell. I will prepare the order and you confirm it.',
}

/** Experimental (flag: chat-public-trading). Orders are only sent when the investor confirms a card. */
export const TradingChat = ({ onOrderPlaced }: { onOrderPlaced: () => void }) => {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<Entry[]>([GREETING])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [entries, busy, open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const addNote = (content: string) =>
    setEntries((list) => [...list, { role: 'assistant', content }])

  const send = async (event: React.FormEvent) => {
    event.preventDefault()
    const text = input.trim()
    if (!text || busy) return
    const next: Entry[] = [...entries, { role: 'user', content: text }]
    setEntries(next)
    setInput('')
    setBusy(true)
    setError('')

    const history = next
      .slice(1) // the greeting is local only
      .slice(-MAX_HISTORY)
      .map(({ role, content }) => ({ role, content }))
    while (history.length && history[0].role !== 'user') history.shift()

    const res = await tradingServices.chat(history)
    setBusy(false)
    if (res?.status === 200) {
      const { reply, cards } = res.data as ChatReply
      setEntries((list) => [...list, { role: 'assistant', content: reply, cards }])
    } else {
      setError(errorMessage(res, 'The assistant is unavailable right now.'))
    }
  }

  // The window stays mounted while closed so the conversation is kept.
  return (
    <div className='trading-chat'>
      {open ? null : (
        <button
          type='button'
          className='trading-chat__launcher'
          onClick={() => setOpen(true)}
          aria-label='Open trade chat'
          data-testid='trading-chat-launcher'
        >
          Trade via chat
          <span className='trading-chat__badge'>Experimental</span>
        </button>
      )}
      <section
        className='trading-chat__window'
        data-testid='trading-chat'
        role='dialog'
        aria-label='Trade via chat'
        hidden={!open}
      >
        <header className='trading-chat__header'>
          <h2>
            Trade via chat <span className='trading-chat__badge'>Experimental</span>
          </h2>
          <button
            type='button'
            className='trading-chat__close'
            onClick={() => setOpen(false)}
            aria-label='Close chat'
          >
            ×
          </button>
        </header>
        <div className='trading-chat__log' role='log' aria-live='polite'>
          {entries.map((entry, i) => (
            <div key={i} className={`trading-chat__msg trading-chat__msg--${entry.role}`}>
              <p>{entry.content}</p>
              {entry.cards?.map((card, j) =>
                card.type === 'order' ? (
                  <OrderCard key={j} card={card} onResult={addNote} onPlaced={onOrderPlaced} />
                ) : (
                  <InfoCard key={j} card={card} />
                ),
              )}
            </div>
          ))}
          {busy ? <p className='trading-chat__typing'>Thinking…</p> : null}
          <div ref={endRef} />
        </div>
        {error ? (
          <p className='trading-page__error' role='alert'>
            {error}
          </p>
        ) : null}
        <form className='trading-chat__form' onSubmit={send}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='e.g. buy 2 shares of Netflix'
            aria-label='Message'
            maxLength={2000}
          />
          <button type='submit' className='trading-page__btn' disabled={busy || !input.trim()}>
            Send
          </button>
        </form>
        <p className='trading-page__disclaimer'>
          AI-generated and can make mistakes. This is general information, not investment advice.
          Check every order before you confirm it.
        </p>
      </section>
    </div>
  )
}
